import React, { useEffect, useState } from 'react'
import {
  useDebounce,
  useFetchAllRoles,
  useFetchAllUsers,
  useFetchUserInvites,
} from '../../../hooks'
import { PageHeader } from '../PageHeader'
import { UsersTable } from './user-table/UsersTable'
import { InvitesTable } from './invites-table/InvitesTable'
import Connect from 'app/redux/connect'
import { Connected } from 'app/interface/redux'
import UserCreate from './modals/UserCreate'
import { DeletedUsersModal } from './modals/DeletedUsersModal'
import { Permissions } from '@marketing-milk/interfaces'
import { TabPanel } from 'components/generic/tab-panel'
import { keyBy } from 'lodash'

const Users = ({ ...props }: Connected) => {
  const [filter, setFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const { debouncedValue } = useDebounce(searchTerm, 1000)

  const businessLookUp = keyBy(props.data.generic.businesses.data, 'id')

  const userHook = useFetchAllUsers()
  const { data: auth0Roles, isLoading: loadingRoles } = useFetchAllRoles()
  const inviteHook = useFetchUserInvites()

  const canManageRoles = props.core.session.user!.permissions.includes(Permissions.ManageRoles)

  // Update invite search when debouncedValue changes
  useEffect(() => {
    inviteHook.setPaginationParams((prevParams: any) => {
      const newParams = {
        ...prevParams,
        searchTerm: debouncedValue.trim().toLowerCase(),
        page: 1,
      }
      return newParams
    })
  }, [debouncedValue, inviteHook.setPaginationParams])

  const refetchUsers = () => {
    userHook.refetch()
  }

  const refetchAll = () => {
    userHook.refetch()
    inviteHook.refetch()
  }

  const handleUserCreated = () => {
    refetchAll()
    setActiveTab(1) // Switch to invited users tab (index 1)
  }

  useEffect(() => {
    userHook.setPaginationParams(prevParams => ({
      ...prevParams,
      searchTerm: debouncedValue.trim().toLowerCase(),
      page: 1,
    }))
  }, [debouncedValue, userHook.setPaginationParams])

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Users"
        filterValue={filter}
        onChangeFilter={value => {
          setSearchTerm(value)
          setFilter(value)
        }}
      >
        <div style={{ flex: 'none' }}>
          <UserCreate
            canManageRoles={canManageRoles}
            auth0Roles={auth0Roles}
            isLoading={loadingRoles}
            onSave={handleUserCreated}
            businesses={props.data.generic.businesses.data}
            pods={props.data.generic.pods.data}
          />
        </div>
        <div className="ml-2">
          <DeletedUsersModal onRestoreDeletedUsers={refetchUsers} />
        </div>
      </PageHeader>

      <TabPanel
        activeSection={activeTab}
        sections={[
          {
            title: `Users (${userHook?.count || 0})`,
            section: (
              <UsersTable
                canManageRoles={canManageRoles}
                users={userHook?.users}
                userHook={userHook}
              />
            ),
          },
          {
            title: `User Invitations (${inviteHook.count || 0})`,
            section: (
              <InvitesTable
                invites={inviteHook.invites}
                inviteHook={inviteHook}
                businessLookUp={businessLookUp}
              />
            ),
          },
        ]}
      />
    </div>
  )
}

export default Connect(Users)
